/**
 * ChatRow Component
 *
 * Displays a chat session in row format using BaseRow layout.
 * Shows title, last message preview, timestamp, and fast actions.
 */

import React, { useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';
import type { ActionType } from '../../types/row.types';

// =============================================================================
// TYPES
// =============================================================================

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface ChatRowProps {
  entity: ChatSession;
  isActive?: boolean;
  onTitleClick?: (session: ChatSession) => void;
  onActionClick?: (actionType: string, sessionId: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ChatRow({ entity: session, isActive, onTitleClick, onActionClick }: ChatRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware time formatting
  const formatTime = (isoDate: string): string => {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common.now');
    if (diffMins < 60) return t('common.minutesAgo', { count: diffMins });
    if (diffHours < 24) {
      return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    if (diffDays < 7) {
      return date.toLocaleDateString(locale, {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
    });
  };

  // Primary action state (archived)
  const [isArchived, setIsArchived] = useState(false);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(session.id);
  }, [session.id]);

  // Left slot - Primary Action (archive)
  const leftSlot = (
    <PrimaryAction
      type="archive"
      active={isArchived}
      onChange={(archived) => {
        setIsArchived(archived);
        onActionClick?.('archive', session.id);
      }}
    />
  );

  // Get available actions for chat sessions
  const availableActions = useMemo(() => {
    // Default actions for chat: task, note, file, event
    const defaultActions: ActionType[] = ['task', 'note', 'file', 'event'];
    try {
      const registryActions = getAvailableActions(session.id);
      return registryActions.length > 0 ? registryActions : defaultActions;
    } catch {
      return defaultActions;
    }
  }, [session.id]);

  // Build meta content
  const metaContent = useMemo(() => {
    const lastMessage = session.messages[session.messages.length - 1];
    const preview = lastMessage
      ? lastMessage.content.length > 50
        ? lastMessage.content.substring(0, 50) + '...'
        : lastMessage.content
      : t('sidebar.newChat');

    const timeStr = formatTime(session.updatedAt);

    return (
      <div style={styles.metaContainer}>
        <span style={styles.preview}>{preview}</span>
        <span style={styles.separator}>·</span>
        <span style={styles.time}>{timeStr}</span>
      </div>
    );
  }, [session, t, formatTime]);

  // Icon with active state color
  const icon = (
    <MessageSquare
      size={16}
      style={{ color: isActive ? '#3b82f6' : '#9ca3af' }}
    />
  );

  return (
    <BaseRow
      entity={session}
      entityType="chat"
      leftSlot={leftSlot}
      icon={icon}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={isArchived}
      className={isActive ? 'chat-row--active' : ''}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// formatTime is now handled inside the component with locale awareness

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
  } as React.CSSProperties,
  preview: {
    color: '#666',
    fontStyle: 'italic',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
  } as React.CSSProperties,
  time: {
    color: '#999',
    flexShrink: 0,
  } as React.CSSProperties,
};
